<template>
  <div class="w-full space-y-4 text-slate-100">
    <!-- TOP BAR -->
    <div
      class="min-w-0 border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="flex flex-col gap-4 px-4 py-4 sm:px-5 sm:py-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <!-- LEFT -->
          <div class="min-w-0">
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
              <ol role="list" class="flex items-center gap-2 text-xs text-slate-400">
                <li class="flex items-center">
                  <router-link
                    to="/hub"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-200 transition hover:bg-slate-600"
                  >
                    <HomeIcon class="h-4 w-4" aria-hidden="true" />
                    <span class="sr-only">Home</span>
                  </router-link>
                </li>

                <li class="flex items-center gap-2">
                  <ChevronRightIcon class="h-4 w-4 text-slate-500" />
                  <span class="font-medium text-slate-300">Zamówienia paliwowe</span>
                </li>
              </ol>
            </nav>

            <div class="flex items-center sm:hidden">
              <router-link
                to="/hub"
                class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-600"
              >
                <ArrowLeftIcon class="h-4 w-4" />
                Cofnij
              </router-link>
            </div>

            <div class="mt-3">
              <div class="text-lg font-semibold tracking-tight text-white">
                Zarządzanie zamówieniami paliwowymi
              </div>
              <div class="mt-1 text-xs text-slate-400">
                Lista bieżących i archiwalnych zamówień wraz z możliwością podglądu i obsługi.
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="flex shrink-0 items-center">
            <button
              type="button"
              @click.prevent="addOrder"
              class="inline-flex items-center gap-2 rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-xs font-semibold text-slate-100 shadow-sm transition hover:bg-slate-600"
            >
              <PlusIcon class="h-4 w-4" />
              Dodaj zamówienie
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLE CARD -->
    <div
      class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="border-b border-slate-700 px-4 py-4 sm:px-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div class="min-w-0">
            <div class="text-sm font-semibold text-white">
              {{ showArchive ? "Archiwum zamówień" : "Zamówienia bieżące" }}
            </div>
            <div class="mt-1 text-xs text-slate-400">
              Łącznie pozycji: {{ sortedOrders.length }}
            </div>
          </div>

          <div class="shrink-0">
            <nav
              class="inline-flex rounded-2xl border border-slate-700 bg-slate-900 bg-opacity-50 p-1 shadow-inner"
              aria-label="Tabs"
            >
              <button
                type="button"
                @click.prevent="changeList('current')"
                :class="[
                  !showArchive
                    ? 'bg-slate-700 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                  'rounded-xl px-4 py-2 text-sm font-semibold transition'
                ]"
              >
                Zamówienia bieżące
              </button>

              <button
                type="button"
                @click.prevent="changeList('archive')"
                :class="[
                  showArchive
                    ? 'bg-slate-700 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                  'rounded-xl px-4 py-2 text-sm font-semibold transition'
                ]"
              >
                Archiwum zamówień
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div class="px-4 py-4 sm:px-5">
        <div v-if="!queryLoading" class="overflow-hidden rounded-2xl border border-slate-700">
  <div class="w-full min-w-0 overflow-hidden">
    <table class="w-full divide-y divide-slate-700" style="table-layout: fixed; width: 100%;">
      <colgroup>
        <col style="width: 5%;" />    <!-- ID -->
        <col style="width: 6%;" />    <!-- Twórca -->
        <col style="width: 17%;" />   <!-- Klient -->
        <col style="width: 9%;" />    <!-- Region -->
        <col style="width: 13%;" />   <!-- Pref. data dostawy -->
        <col style="width: 11%;" />   <!-- Status -->
        <col style="width: 13%;" />   <!-- Produkt (skrócona nazwa) -->
        <col style="width: 6%;" />    <!-- Ilość -->
        <col style="width: 9%;" />    <!-- SENT -->
        <col style="width: 6%;" />    <!-- Zafakturowane -->
        <col style="width: 5%;" />    <!-- Akcje -->
      </colgroup>

      <thead class="bg-slate-700 bg-opacity-70">
        <tr>
          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              ID
              <ChevronDownIcon
                v-if="sort.pickedColumn === '_id' && sort.type === 'asc'"
                @click.prevent="setSort('_id', 'desc')"
                :class="iconCls('_id')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('_id', 'asc')"
                :class="iconCls('_id')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              Twórca
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'creatorType' && sort.type === 'asc'"
                @click.prevent="setSort('creatorType', 'desc')"
                :class="iconCls('creatorType')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('creatorType', 'asc')"
                :class="iconCls('creatorType')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">
            <div class="flex items-center gap-1">
              Klient
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'clientName' && sort.type === 'asc'"
                @click.prevent="setSort('clientName', 'desc')"
                :class="iconCls('clientName')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('clientName', 'asc')"
                :class="iconCls('clientName')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              Region dostawy
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'region' && sort.type === 'asc'"
                @click.prevent="setSort('region', 'desc')"
                :class="iconCls('region')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('region', 'asc')"
                :class="iconCls('region')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              Pref. data dostawy
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'deliveryFromSort' && sort.type === 'asc'"
                @click.prevent="setSort('deliveryFromSort', 'desc')"
                :class="iconCls('deliveryFromSort')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('deliveryFromSort', 'asc')"
                :class="iconCls('deliveryFromSort')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              Status
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'statusSort' && sort.type === 'asc'"
                @click.prevent="setSort('statusSort', 'desc')"
                :class="iconCls('statusSort')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('statusSort', 'asc')"
                :class="iconCls('statusSort')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">
            <div class="flex items-center gap-1">
              Produkt
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'product' && sort.type === 'asc'"
                @click.prevent="setSort('product', 'desc')"
                :class="iconCls('product')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('product', 'asc')"
                :class="iconCls('product')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              Ilość
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'qtySort' && sort.type === 'asc'"
                @click.prevent="setSort('qtySort', 'desc')"
                :class="iconCls('qtySort')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('qtySort', 'asc')"
                :class="iconCls('qtySort')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              SENT
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'sentSort' && sort.type === 'asc'"
                @click.prevent="setSort('sentSort', 'desc')"
                :class="iconCls('sentSort')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('sentSort', 'asc')"
                :class="iconCls('sentSort')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100">
            <div class="flex items-center gap-1">
              Zafakturowane
              <ChevronDownIcon
                v-if="sort.pickedColumn === 'invoicedSort' && sort.type === 'asc'"
                @click.prevent="setSort('invoicedSort', 'desc')"
                :class="iconCls('invoicedSort')"
                aria-hidden="true"
              />
              <ChevronUpIcon
                v-else
                @click.prevent="setSort('invoicedSort', 'asc')"
                :class="iconCls('invoicedSort')"
                aria-hidden="true"
              />
            </div>
          </th>

          <th class="px-3 py-3.5 text-right">
            <span class="sr-only">Akcje</span>
          </th>
        </tr>
      </thead>

      <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
        <tr
          v-for="row in sortedOrders"
          :key="row._id"
          class="transition hover:bg-slate-700 hover:bg-opacity-30"
        >
          <td class="px-3 py-4 text-xs text-slate-100">
            <div class="truncate">
              <span
                v-tooltip="`${row._id}`"
                @click.prevent="copyToClipboard(row._id)"
                class="cursor-pointer font-semibold text-blue-300 transition hover:text-blue-200"
              >
                Kopiuj
              </span>
            </div>
          </td>

          <td class="px-3 py-4 text-xs text-slate-200">
            <div v-tooltip="row.creatorType" class="truncate">{{ row.creatorType }}</div>
          </td>

          <td class="px-3 py-4 text-xs text-slate-100">
            <div v-tooltip="row.clientName" class="truncate">
              {{ row.clientName }}
            </div>
          </td>

          <td class="px-3 py-4 pr-4 text-xs text-slate-200">
            <div v-tooltip="row.region" class="truncate">{{ row.region }}</div>
          </td>

          <td class="px-3 py-4 text-xs text-slate-200">
            <div v-tooltip="row.deliveryDateDisplay" class="truncate">{{ row.deliveryDateDisplay }}</div>
          </td>

          <td class="px-3 py-4 text-xs text-slate-200">
            <div v-if="row.statusSort === 1 && !showArchive">
              <button
                type="button"
                @click.prevent="acceptOrder(row._id)"
                class="inline-flex items-center rounded-xl border border-indigo-500 border-opacity-30 bg-indigo-500 bg-opacity-15 px-2 py-1.5 text-xs font-semibold text-indigo-200 transition hover:bg-opacity-25"
              >
                Przyjmij
              </button>
            </div>

            <div v-else v-tooltip="fuelOrderStatusLabel(row.statusSort)" class="truncate">
              <span :class="fuelOrderStatusColor(row.statusSort)" class="font-semibold">
                {{ fuelOrderStatusLabel(row.statusSort) }}
              </span>
            </div>
          </td>

          <td class="px-3 py-4 text-xs text-slate-200">
            <div v-tooltip="row.productFull" class="truncate">
              {{ row.product }}
            </div>
          </td>

          <td class="px-3 py-4 text-xs text-slate-200">
            <div v-tooltip="row.qtyDisplay" class="truncate">{{ row.qtyDisplay }}</div>
          </td>

          <!-- SENT badge — truncate z tooltipem (BUSINESS_ERROR pokazuje powód). -->
          <td class="px-3 py-4 text-xs">
            <span
              v-tooltip="row.sentTooltip"
              :class="['block max-w-full truncate rounded-xl px-2.5 py-1 text-left font-semibold', row.sentColor]"
            >
              {{ row.sentLabel }}
            </span>
          </td>

          <!-- Zafakturowane (check / x) -->
          <td class="whitespace-nowrap px-3 py-4 text-xs">
            <CheckIcon
              v-if="row.invoicedFlag"
              class="h-5 w-5 text-emerald-400"
              v-tooltip="'Zafakturowane'"
              aria-hidden="true"
            />
            <XMarkIcon
              v-else
              class="h-5 w-5 text-slate-500"
              v-tooltip="'Nie zafakturowane'"
              aria-hidden="true"
            />
          </td>

          <td class="px-3 py-4 text-right text-sm font-medium select-none">
            <div class="flex items-center justify-end">
              <button
                :v-tooltip="showArchive ? 'Podgląd' : 'Edycja'"
                type="button"
                @click.prevent="editOrder(row._id)"
                class="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-80 transition hover:bg-slate-600"
              >
                <PencilSquareIcon
                  class="h-4 w-4 text-slate-200"
                  aria-hidden="true"
                />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <EmptyState v-if="ordersList.length === 0"></EmptyState>
  </div>
</div>

        <div v-else class="flex items-center justify-center py-10">
          <div class="spinner"></div>
        </div>

        <div class="mt-5 flex items-center justify-center" v-if="!queryLimitReached && !queryLoading">
          <button
            type="button"
            @click.prevent="loadMore(10)"
            class="inline-flex items-center rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
          >
            Załaduj więcej
          </button>
        </div>
      </div>
    </div>

    <!-- Modal dodawania zamówienia -->
    <ModalNewFuelOrder
      v-if="showAddModal"
      :show="showAddModal"
      @closeOrderModal="showAddModal = false"
    />

    <!-- Modal edycji zamówienia (tylko dla bieżących) -->
    <ModalManageFuelOrder
      v-if="showEditModal && pickedOrder && !showArchive"
      :show="showEditModal"
      :managedOrder="pickedOrder"
      @closeOrderModal="showEditModal = false; pickedOrder = null"
      @reloadOrders="downloadOrders"
    />

    <!-- Modal podglądu zamówienia (archiwum) -->
    <ModalPreviewFuelOrder
      v-if="showPreviewModal && pickedOrder && showArchive"
      :show="showPreviewModal"
      :order="pickedOrder"
      @closePreview="showPreviewModal = false; pickedOrder = null"
    />
  </div>
</template>

<script>
import { createSentApi } from '@/services/sentApi'
import { getSentLabel, getSentColor, getSentSortKey, getSentTooltip } from '@/utils/sentStatus'
import {
  HomeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  XMarkIcon,
  ChevronRightIcon,
  PlusIcon,
  ArrowLeftIcon
} from '@heroicons/vue/24/solid'
import { PencilSquareIcon } from '@heroicons/vue/24/outline'
import EmptyState from '@/components/EmptyState.vue'
import { db } from '@/firebase/gfbconf.js'
import ModalNewFuelOrder from '@/components/ModalNewFuelOrder.vue'
import ModalManageFuelOrder from '@/components/ModalManageFuelOrder.vue'
import ModalPreviewFuelOrder from '@/components/ModalPreviewFuelOrder.vue'

export default {
  name: 'ManageFuelOrders',
  components: {
    EmptyState,
    HomeIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ChevronRightIcon,
    PlusIcon,
    ArrowLeftIcon,
    PencilSquareIcon,
    CheckIcon,
    XMarkIcon,
    ModalNewFuelOrder,
    ModalManageFuelOrder,
    ModalPreviewFuelOrder
  },
  data() {
    return {
      showArchive: false,
      queryLoading: false,
      queryLimitReached: false,
      queryLimit: 50,
      ordersList: [],
      dbListeners: [],
      showAddModal: false,
      showEditModal: false,
      showPreviewModal: false,
      pickedOrder: null,
      sort: {
        pickedColumn: 'clientName',
        type: 'asc'
      },
      // Cache statusów SENT z urzad-api: { documentId → status string }
      sentStatuses: {},
    }
  },

  created() {
    if (!this.$store.state.userData.permissions.admin) {
      this.$router.push('/hub')
      return
    }
    this.downloadOrders()
  },

  computed: {
    mappedOrders() {
      const nf = new Intl.NumberFormat('pl-PL')
      return this.ordersList.map((o) => {
        const id = o.id || o._id || ''
        const creatorType = o.createdByClient ? 'Klient' : 'Obsługa'
        const clientName = o.client?.invoiceAddress?.name || '—'
        const region = o.deliveryAddress?.regionName || 'Nie przypisano'
        const accepted = !!o.acceptedByAdmin
        const delivered = !!o.delivered
        const canceled = !!o.canceled

        // Nowy system: użyj o.status jeśli istnieje, fallback na stare booleany
        let statusSort = o.status
        if (statusSort == null) {
          statusSort = 1
          if (canceled) statusSort = 6
          else if (delivered) statusSort = 5
          else if (o.isInRoute) statusSort = 3
          else if (accepted) statusSort = 2
        }

        const supplier = o.invoice?.isLauronInvoice ? 'Lauron' : 'WBtank'

        let invoiceDisplay = 'Oczekuje na fakturę'
        if (o.invoice?.invoiced) {
          invoiceDisplay = o.invoice.invoiceNumber || 'Zafakturowano o nieznanym numerze'
        }

        // Pełna nazwa do tooltipu, skrócona do tabeli (kolumna jest wąska).
        // "Shell FuelSave Diesel CN 27102011" → "FuelSave Diesel"
        const productFull = o.orderedFuel?.fuelName || '—'
        const product = productFull
          .replace(/^Shell\s+/i, '')
          .replace(/\s+CN\s*\d+.*$/i, '')
          .trim() || productFull

        const qty = Number(o.orderedFuel?.quantityLiters ?? NaN)
        const qtyDisplay = Number.isFinite(qty) ? `${nf.format(qty)} L` : '—'
        const qtySort = Number.isFinite(qty) ? qty : -Infinity

        const fromUnix = Number(o.deliveryDate?.from?.unix ?? NaN)
        const fromText = o.deliveryDate?.from?.asText || null
        const toText = o.deliveryDate?.to?.asText || null

        let deliveryDateDisplay = '—'
        if (fromText && toText && fromText !== toText) {
          deliveryDateDisplay = `${fromText} – ${toText}`
        } else if (fromText) {
          deliveryDateDisplay = fromText
        } else if (toText) {
          deliveryDateDisplay = toText
        }

        const deliveryFromSort = Number.isFinite(fromUnix) ? fromUnix : -Infinity

        // SENT — cache trzyma { status, errors }. Tooltip dla BUSINESS_ERROR pokazuje
        // ostatni error.description (z NPP / SENT_500), żeby nie trzeba było otwierać historii.
        const sentDocId = o.invoice?.sent?.documentId || null
        const sentEntry = sentDocId ? (this.sentStatuses[sentDocId] || null) : null
        const sentStatus = sentEntry?.status || null
        const sentErrors = sentEntry?.errors || []
        const sentLabel = getSentLabel(sentStatus)
        const sentColor = getSentColor(sentStatus)
        const sentSort = getSentSortKey(sentStatus)
        const sentTooltip = getSentTooltip(sentStatus, sentErrors)

        const invoicedFlag = !!o.invoice?.invoiced
        const invoicedSort = invoicedFlag ? 1 : 0

        return {
          _id: id,
          raw: o,
          creatorType,
          clientName,
          region,
          supplier,
          invoiceDisplay,
          product,
          productFull,
          qtyDisplay,
          qtySort,
          acceptedByAdmin: accepted,
          delivered,
          canceled,
          statusSort,
          deliveryDateDisplay,
          deliveryFromSort,
          sentLabel,
          sentColor,
          sentSort,
          sentTooltip,
          invoicedFlag,
          invoicedSort,
        }
      })
    },

    sortedOrders() {
      const list = [...this.mappedOrders]
      const col = this.sort.pickedColumn
      const dir = this.sort.type === 'asc' ? 1 : -1

      return list.sort((a, b) => {
        const va = a?.[col]
        const vb = b?.[col]

        if (col === 'statusSort' || col === 'qtySort' || col === 'deliveryFromSort' || col === 'sentSort' || col === 'invoicedSort') {
          const na = Number.isFinite(+va) ? +va : -Infinity
          const nb = Number.isFinite(+vb) ? +vb : -Infinity
          return (na - nb) * dir
        }

        const sa = (va ?? '').toString().toLowerCase()
        const sb = (vb ?? '').toString().toLowerCase()
        if (sa < sb) return -1 * dir
        if (sa > sb) return 1 * dir
        return 0
      })
    }
  },

  methods: {
    /**
     * Pobiera statusy SENT z urzad-api dla wszystkich zamówień z invoice.sent.documentId.
     * Wynik zapisywany w this.sentStatuses (cache reactive). Wywołane po downloadOrders.
     * Batch po 10 równolegle, z try/catch — pojedynczy błąd nie psuje całego widoku.
     */
    async fetchSentStatuses() {
      const ids = this.ordersList
        .map((o) => o.invoice?.sent?.documentId)
        .filter(Boolean)
      if (ids.length === 0) return
      const baseUrl = this.$store.state.apiComplaiaLink
      const api = createSentApi(baseUrl)
      const next = { ...this.sentStatuses }
      // Batch 10 równolegle. Cache trzyma { status, errors } — errors[] potrzebne
      // do tooltipa BUSINESS_ERROR.
      for (let i = 0; i < ids.length; i += 10) {
        const slice = ids.slice(i, i + 10)
        await Promise.allSettled(
          slice.map(async (id) => {
            try {
              const res = await api.getSentStatus(id)
              next[id] = {
                status: res?.payload?.status || null,
                errors: res?.payload?.errors || [],
              }
            } catch (e) {
              // ignoruj błędy pojedynczych — getSentStatus może zwrócić 404 jeśli rekord nie istnieje
            }
          })
        )
      }
      this.sentStatuses = next
    },

    editOrder(id) {
      const found = this.ordersList.find((o) => o.id === id || o._id === id)
      if (!found) return
      this.pickedOrder = found

      if (this.showArchive) {
        this.showPreviewModal = true
      } else {
        this.showEditModal = true
      }
    },

    fuelOrderStatusLabel(code) {
      switch (code) {
        case 1: return 'Oczekuje'
        case 2: return 'Do realizacji'
        case 3: return 'Oczekuje na wyjazd'
        case 4: return 'W trakcie realizacji'
        case 5: return 'Dostarczone'
        case 6: return 'Anulowane'
        default: return 'Nieznany'
      }
    },

    fuelOrderStatusColor(code) {
      switch (code) {
        case 1: return 'text-yellow-300'
        case 2: return 'text-blue-300'
        case 3: return 'text-indigo-300'
        case 4: return 'text-purple-300'
        case 5: return 'text-green-300'
        case 6: return 'text-red-300'
        default: return 'text-slate-400'
      }
    },

    iconCls(col) {
      return {
        'h-4 w-4 cursor-pointer transition hover:text-blue-300': true,
        'opacity-30': this.sort.pickedColumn !== col,
        'text-slate-300': true
      }
    },

    setSort(column, type) {
      this.sort.pickedColumn = column
      this.sort.type = type
    },

    changeList(type) {
      const targetArchive = type === 'archive'
      if (this.showArchive !== targetArchive) {
        this.showArchive = targetArchive
        this.queryLimit = 50
        this.unsubscribeAll()
        this.downloadOrders()
      }
    },

    unsubscribeAll() {
      this.dbListeners.forEach((unsub) => {
        if (typeof unsub === 'function') unsub()
      })
      this.dbListeners = []
    },

    downloadOrders() {
      this.queryLoading = true
      this.ordersList = []
      this.queryLimitReached = false

      const collect = {}
      let expectedSnapshots = 2
      let gotSnapshots = 0

      const handleSnapshot = (qs) => {
        qs.forEach((doc) => {
          collect[doc.id] = { id: doc.id, ...doc.data() }
        })
        gotSnapshots++
        this.ordersList = Object.values(collect)
        if (gotSnapshots >= expectedSnapshots) {
          this.queryLoading = false
          this.queryLimitReached = this.ordersList.length < this.queryLimit
          // Po pełnym ładowaniu listy — pobierz statusy SENT z urzad-api (cache w sentStatuses)
          this.fetchSentStatuses().catch(() => {})
        }
      }

      const handleError = () => {
        gotSnapshots++
        if (gotSnapshots >= expectedSnapshots) {
          this.queryLoading = false
        }
      }

      const colRef = db.collection('FuelOrders')

      if (this.showArchive) {
        // Archiwum: Dostarczone (5) + Anulowane (6)
        const q1 = colRef.where('status', 'in', [5, 6]).limit(this.queryLimit)
        const unsub1 = q1.onSnapshot(handleSnapshot, handleError)
        this.dbListeners.push(unsub1)

        // Fallback dla starych rekordów bez pola status
        const q2 = colRef
          .where('canceled', '==', true)
          .limit(this.queryLimit)
        const unsub2 = q2.onSnapshot(handleSnapshot, handleError)
        this.dbListeners.push(unsub2)
      } else {
        // Aktualne: Oczekuje (1), Do realizacji (2), Oczekuje na wyjazd (3), W trakcie (4)
        const q1 = colRef
          .where('status', 'in', [1, 2, 3, 4])
          .limit(this.queryLimit)
        const unsub1 = q1.onSnapshot(handleSnapshot, handleError)
        this.dbListeners.push(unsub1)

        // Dostarczone ale nie zafakturowane (status 5) — też w aktualnych
        const q2 = colRef
          .where('status', '==', 5)
          .where('invoice.invoiced', '==', false)
          .limit(this.queryLimit)
        const unsub2 = q2.onSnapshot(handleSnapshot, handleError)
        this.dbListeners.push(unsub2)
      }
    },

    loadMore(amount) {
      this.unsubscribeAll()
      this.queryLimit = this.queryLimit + amount
      this.downloadOrders()
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$store.commit('setNotification', {
          show: true,
          head: 'Skopiowano do schowka!',
          subheader: ``,
          success: true
        })
      })
    },

    async acceptOrder(id) {
      if (!id) return
      try {
        await db.collection('FuelOrders').doc(id).update({
          acceptedByAdmin: true,
          status: 2
        })
        this.$store.commit('setNotification', {
          show: true,
          head: 'Zamówienie przyjęte!',
          subheader: 'Zamówienie zostało oznaczone jako przyjęte do realizacji.',
          success: true
        })
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd podczas aktualizacji',
          subheader: 'Nie udało się przyjąć zamówienia.',
          success: false
        })
      }
    },

    addOrder() {
      this.showAddModal = true
    }
  },

  beforeUnmount() {
    this.unsubscribeAll()
  }
}
</script>

<style lang="scss" scoped>
.spinner {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 5px solid #475569;
  animation: spinner-bulqg1 1.3s infinite linear alternate, spinner-oaa3wk 2.6s infinite linear;
}

@keyframes spinner-bulqg1 {
  0% { clip-path: polygon(50% 50%, 0 0, 50 0, 50 0, 50 0, 50 0, 50 0); }
  12.5% { clip-path: polygon(50% 50%, 0 0, 50 0, 100 0, 100 0, 100 0, 100 0); }
  25% { clip-path: polygon(50% 50%, 0 0, 50 0, 100 0, 100 100, 100 100, 100 100); }
  50% { clip-path: polygon(50% 50%, 0 0, 50 0, 100 0, 100 100, 50 100, 0 100); }
  62.5% { clip-path: polygon(50% 50%, 100 0, 100 0, 100 0, 100 100, 50 100, 0 100); }
  75% { clip-path: polygon(50% 50%, 100 100, 100 100, 100 100, 100 100, 50 100, 0 100); }
  100% { clip-path: polygon(50% 50%, 50 100, 50 100, 50 100, 50 100, 50 100, 0 100); }
}

@keyframes spinner-oaa3wk {
  0% { transform: scaleY(1) rotate(0deg); }
  49.99% { transform: scaleY(1) rotate(135deg); }
  50% { transform: scaleY(-1) rotate(0deg); }
  100% { transform: scaleY(-1) rotate(-135deg); }
}
</style>